import React, { useEffect, useState, useCallback } from "react";
import { MessageCircle, Send, Sun, Loader2, CheckCircle2, AlertTriangle, Clock, Smartphone, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { ModuleHeader } from "../components/intel/IntelKit";
import Spinner from "../components/Spinner";

const Toggle = ({ checked, onChange, disabled, testid }) => (
  <button type="button" role="switch" aria-checked={checked} disabled={disabled} data-testid={testid}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${disabled ? "bg-slate-200 cursor-not-allowed" : checked ? "bg-emerald-500" : "bg-slate-300"}`}>
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

export default function CoreConnect() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [briefSaving, setBriefSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(null);
  const [s, setS] = useState(null);
  const [configured, setConfigured] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lastSentAt, setLastSentAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    const res = await api.connectGetSettings();
    if (res.ok) {
      setS(res.data.settings); setConfigured(!!res.data.delivery_configured);
      setLastSentAt(res.data.last_sent_at || null);
    } else {
      // Phase 5 fix: a transient network/API failure is NOT the same as a
      // legitimate empty state — the backend always returns defaults for a
      // real customer, so a failure here means retry, not "nothing here."
      setLoadError(res.error || "Temporary error loading C-ORE Connect.");
    }
    setLoading(false);
    // Phase 5/6 fix (2026-08-09, found by testing_agent_v4): /connect/preview
    // runs the full decision-engine pipeline just to build brief text and
    // can take considerably longer than the lightweight /connect/settings
    // call above (16-17s measured) — it must NEVER gate this page's
    // loading/error state (that coupling was itself causing client-side
    // timeouts and "Could not load" flakiness). It's fetched independently,
    // best-effort, and only ever adds bonus preview content if it lands.
    api.connectPreview().then((prev) => { if (prev.ok) setPreview(prev.data); });
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true); setStatus(null);
    const res = await api.connectSaveSettings({
      whatsapp_number: s.whatsapp_number,
      morning_brief_on: s.morning_brief_on,
      morning_brief_time: s.morning_brief_time,
      timezone: s.timezone || "Asia/Kolkata",
    });
    setSaving(false);
    if (res.ok) { setStatus({ ok: true, msg: "Daily Brief preferences saved." }); load(); }
    else setStatus({ ok: false, msg: res.error || "Could not save preferences." });
  };

  // Phase 2/3 fix: the toggle itself is the ONE canonical action the user
  // expects to persist immediately on click — it must never be purely a
  // local/optimistic UI flip. Previously this toggle only called `set()`
  // (local state), so it silently reverted to whatever was last actually
  // saved the moment the page reloaded/remounted — the exact "toggle
  // switches back OFF" symptom reported. It now saves to the backend
  // immediately, and only reflects the value the backend CONFIRMS was
  // persisted; on failure it is put back to the previous value with a
  // clear error, never left showing a false ON.
  const toggleBrief = async (next) => {
    const previous = !!s.morning_brief_on;
    setBriefSaving(true); setStatus(null);
    set("morning_brief_on", next); // immediate visual feedback while saving
    const res = await api.connectSaveSettings({
      whatsapp_number: s.whatsapp_number,
      morning_brief_on: next,
      morning_brief_time: s.morning_brief_time,
      timezone: s.timezone || "Asia/Kolkata",
    });
    setBriefSaving(false);
    if (res.ok) {
      set("morning_brief_on", !!res.data.settings.morning_brief_on);
      setStatus({ ok: true, msg: next ? "Daily Brief turned ON and saved." : "Daily Brief turned OFF and saved." });
    } else {
      set("morning_brief_on", previous); // revert — never claim a failed save succeeded
      setStatus({ ok: false, msg: res.error || "Could not save this change. Daily Brief was NOT changed." });
    }
  };

  const sendTest = async () => {
    setTesting(true); setStatus(null);
    const res = await api.connectTestSend("brief");
    setTesting(false);
    if (res.ok && res.data?.ok) setStatus({ ok: true, msg: "Test Daily Business Brief sent to WhatsApp." });
    else setStatus({ ok: false, msg: res.data?.error || res.data?.result?.error || res.error || "Test send failed." });
  };

  // Phase 6 fix: honest delivery status, computed from real data only —
  // never a fabricated "last update"/"next update" timestamp. Sourced
  // from the fast /connect/settings response, not the slow /preview one.
  const lastSentLabel = lastSentAt
    ? new Date(lastSentAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
    : "Not sent yet";
  const nextSlotLabel = (() => {
    if (!s?.morning_brief_on || !s?.morning_brief_time) return null;
    try {
      const [hh, mm] = s.morning_brief_time.split(":").map(Number);
      const tz = s.timezone || "Asia/Kolkata";
      const nowParts = new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(new Date());
      const nowH = Number(nowParts.find((p) => p.type === "hour").value);
      const nowM = Number(nowParts.find((p) => p.type === "minute").value);
      const minutesUntil = ((hh * 60 + mm) - (nowH * 60 + nowM) + 1440) % 1440;
      const next = new Date(Date.now() + minutesUntil * 60000);
      return next.toLocaleString("en-IN", { weekday: "long", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch (_e) { return null; }
  })();

  if (loading) return <div className="p-8" data-testid="connect-loading"><Spinner /></div>;
  if (loadError) return (
    <div className="p-8 max-w-md" data-testid="connect-load-error">
      <div className="flex items-center gap-2 text-[13px] text-rose-700"><AlertTriangle className="h-4 w-4" /> {loadError}</div>
      <button onClick={load} data-testid="connect-retry-btn" className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3.5 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50">
        Retry
      </button>
    </div>
  );
  if (!s) return <div className="p-8 text-sm text-slate-500" data-testid="connect-empty">Could not load C-ORE Connect.</div>;

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-4xl" data-testid="core-connect-page">
      <ModuleHeader overline="Daily Intelligence" title="C-ORE Connect" testid="connect-header"
        right={<span className="inline-flex items-center gap-1.5"><MessageCircle className="h-4 w-4 text-emerald-600" /> WhatsApp</span>} />
      <p className="text-[13px] text-slate-500 -mt-3">Start every morning knowing what matters most in your business.</p>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="flex gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 border border-emerald-100"><ShieldCheck className="h-5 w-5 text-emerald-700" /></span>
            <div><div className="text-[14px] font-semibold text-[#1E3A5F]">C-ORE on WhatsApp</div>
              <p className="text-[12.5px] text-slate-500 mt-1">Your Daily Business Brief is delivered securely from C-ORE. No API setup is required.</p></div>
          </div>
          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${configured ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            {configured ? "Ready" : "Setup required"}
          </span>
        </div>
      </div>

      {status && <div className={`flex items-center gap-2 rounded-lg px-4 py-2.5 text-[13px] border ${status.ok ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
        {status.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}{status.msg}
      </div>}

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-4">Daily Brief Preferences</div>
        <div className="pb-4 border-b border-slate-100">
          <div className="flex items-center gap-2 text-[13.5px] font-semibold text-[#1E3A5F]"><Smartphone className="h-4 w-4" /> Recipient WhatsApp Number</div>
          <input data-testid="connect-number-input" value={s.whatsapp_number || ""} onChange={(e) => set("whatsapp_number", e.target.value)} placeholder="+91XXXXXXXXXX"
            className="mt-2 w-full max-w-sm rounded-lg border border-slate-200 px-3 py-2.5 text-[13px] focus:border-[#1E3A5F] focus:outline-none" />
          <div className="mt-1.5 text-[11.5px] text-slate-400">Include country code. This is where your private morning brief will be delivered.</div>
        </div>

        <div className="py-5 flex items-start justify-between gap-5">
          <div className="flex gap-3"><span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50"><Sun className="h-4 w-4 text-amber-700" /></span>
            <div><div className="text-[13.5px] font-semibold text-[#1E3A5F]">Morning Business Brief</div>
              <div className="text-[12px] text-slate-500 max-w-lg mt-0.5">Business Health, revenue, cash position, the most important alert, and one priority for today. Also includes a Weekly Command Center Digest every Monday with your top priorities for the week.</div>
              <div className="text-[11.5px] text-slate-400 mt-1.5" data-testid="connect-delivery-status">
                Last update: {lastSentLabel}
                {nextSlotLabel && <> · Next scheduled slot: {nextSlotLabel}</>}
              </div>
            </div>
          </div>
          <Toggle testid="connect-brief-toggle" checked={!!s.morning_brief_on} disabled={briefSaving} onChange={toggleBrief} />
        </div>

        <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
          <div><label className="flex items-center gap-1.5 text-[12.5px] font-semibold text-[#1E3A5F]"><Clock className="h-3.5 w-3.5" /> Delivery time</label>
            <input type="time" data-testid="connect-brief-time" value={s.morning_brief_time || "08:00"} onChange={(e) => set("morning_brief_time", e.target.value)} disabled={!s.morning_brief_on}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] disabled:opacity-50" /></div>
          <div><label className="text-[12.5px] font-semibold text-[#1E3A5F]">Timezone</label>
            <select value={s.timezone || "Asia/Kolkata"} onChange={(e) => set("timezone", e.target.value)} disabled={!s.morning_brief_on}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] disabled:opacity-50 bg-white">
              <option value="Asia/Kolkata">India (IST)</option><option value="Asia/Dubai">Dubai (GST)</option><option value="Europe/London">London</option><option value="America/New_York">New York</option><option value="America/Los_Angeles">Los Angeles</option>
            </select></div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button onClick={save} disabled={saving} data-testid="connect-save-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-[#1E3A5F] px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-[#16304d] disabled:opacity-50">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save preferences</button>
          <button onClick={sendTest} disabled={testing || !s.whatsapp_number || !configured} data-testid="connect-test-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2.5 text-[13px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send test brief</button>
        </div>
      </div>

      {preview?.morning_brief && <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="connect-preview">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Today's preview</div>
        <div className="rounded-xl bg-[#075E54]/[0.04] border border-emerald-100 p-4"><pre className="whitespace-pre-wrap font-sans text-[13px] text-slate-700 leading-relaxed">{preview.morning_brief}</pre></div>
      </div>}
      <p className="text-[11.5px] text-slate-400">C-ORE Connect is currently a one-way executive intelligence channel. Replies on WhatsApp are not monitored.</p>
    </div>
  );
}
